import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../../interfaces/client-manager.interface';
import { TimeModel } from '../../../models/primitive/time.model';
import { TimeUtilities } from '../../../utilities/time-utilities';
import { DateModel } from '../../../models/primitive/date.model';
enum MERIDIEM {
    AM = 'AM',
    PM = 'PM',
}
const maxHoursFor12HourFormat = 12;
const inputMaxLength = 2;
const inputMinuteMaxValue = 59;
const maxHoursFor24HourFormat = 23;

@Component({
    selector: 'pace-core-time-manual-input',
    templateUrl: './time-manual-input.component.html',
    styleUrls: ['./time-manual-input.component.scss'],
    standalone: false,
})
export class TimeManualInputComponent implements OnInit, AfterViewInit {
    @Input()
    public model: TimeModel;

    @Input()
    public dateModel: DateModel;

    @Output()
    public modelChanged = new EventEmitter<TimeModel>();

    @ViewChild('hoursInput')
    public hourInputElement: ElementRef;

    @ViewChild('minutesInput')
    private minutesInput: ElementRef;

    public timeInputForm: FormGroup;
    public hoursInputControl: FormControl;
    public minutesInputControl: FormControl;
    public warningText = '';

    public readonly MERIDIEM = MERIDIEM;
    public is12HoursFormat = false;
    public meridiem: MERIDIEM;

    public inputMaxLength = inputMaxLength;
    public inputMinuteMaxValue = inputMinuteMaxValue;
    public maxHoursFor12HourFormat = maxHoursFor12HourFormat;
    public maxHoursFor24HourFormat = maxHoursFor24HourFormat;

    public get hours(): number {
        if (this.hoursInputControl && this.is12HoursFormat) {
            return this.hoursFor12HoursFormat;
        }
        return this.hoursInputControl ? parseInt(this.hoursInputControl.value as string, 10) : NaN;
    }

    public get hoursFor12HoursFormat(): number {
        const hours = parseInt(this.hoursInputControl.value as string, 10);
        if (this.meridiem === MERIDIEM.PM && hours < maxHoursFor12HourFormat) {
            return hours + maxHoursFor12HourFormat;
        }
        return hours;
    }

    public get minutes(): number {
        return this.minutesInputControl ? parseInt(this.minutesInputControl.value as string, 10) : NaN;
    }
    public constructor(
        private readonly formBuilder: FormBuilder,
        public readonly modalInstance: BsModalRef,
        private readonly translate: TranslateService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {
        this.is12HoursFormat = this.clientManager.getTimeFormat().includes('A');
    }

    public ngOnInit(): void {
        this.hoursInputControl = new FormControl(undefined, [
            // eslint-disable-next-line @typescript-eslint/unbound-method
            Validators.required,
            Validators.min(0),
            Validators.max(this.is12HoursFormat ? maxHoursFor12HourFormat : maxHoursFor24HourFormat),
        ]);
        this.minutesInputControl = new FormControl(undefined, [
            // eslint-disable-next-line @typescript-eslint/unbound-method
            Validators.required,
            Validators.min(0),
            Validators.max(inputMinuteMaxValue),
        ]);

        const timeValidator = (): ValidationErrors | null => this.checkValidity();
        this.timeInputForm = this.formBuilder.group(
            {
                hoursInputControl: this.hoursInputControl,
                minutesInputControl: this.minutesInputControl,
            },
            { validators: timeValidator },
        );
    }

    public ngAfterViewInit(): void {
        if (this.hourInputElement) {
            setTimeout(() => {
                (<HTMLInputElement>this.hourInputElement.nativeElement).focus();
            }, 0);
        }
    }

    public onHoursKeyUp(e: KeyboardEvent, input: HTMLInputElement): void {
        // In 1 2 hours format, if hours entered is 12 then meridiem has to be PM
        if (this.is12HoursFormat && this.hours === maxHoursFor12HourFormat && this.meridiem !== MERIDIEM.PM) {
            this.onMeridiemChange(true);
        }
        if (input.value.length === inputMaxLength && e.key !== 'Tab') {
            setTimeout(() => {
                (<HTMLInputElement>this.minutesInput.nativeElement).focus();
            }, 1);
        }
    }

    public onSave(): void {
        if (this.model.value.isValid()) {
            this.model.value.set({ hours: this.hours, minutes: this.minutes, seconds: 0 });
        } else if (this.dateModel) {
            this.model.value = this.dateModel.value;
            this.model.value.set({ hours: this.hours, minutes: this.minutes, seconds: 0 });
        }
        this.modelChanged.emit(new TimeModel(this.model.name, this.model.value, this.model.options));
        this.modalInstance.hide();
    }

    public onMeridiemChange(isPM: boolean): void {
        this.meridiem = isPM ? MERIDIEM.PM : MERIDIEM.AM;
        this.timeInputForm.updateValueAndValidity();
    }

    public checkValidity(): ValidationErrors | null {
        const valueClone = this.model.value.isValid() ? this.model.value.clone() : moment();
        const potentialValue = TimeUtilities.setTimeHtml5(valueClone, `${this.hours}:${this.minutes}:00`);

        const potentialValueDate = potentialValue.clone().startOf('day');
        const minValueDate = this.model.options.minValue.clone().startOf('day');
        const maxValueDate = this.model.options.maxValue.clone().startOf('day');

        let formatPattern = this.clientManager.getTimeFormat();
        if (
            minValueDate.isBefore(maxValueDate) ||
            !(potentialValueDate.isSame(minValueDate) && potentialValueDate.isSame(maxValueDate))
        ) {
            formatPattern = this.clientManager.getDateTimeFormat();
        }

        const errors: ValidationErrors = {};

        if (potentialValue.isBefore(this.model.options.minValue)) {
            errors.minValue = {
                text: `${this.translate.instant('TIME_INPUT_SELECT_TIME_RANGE_TEXT')} ${this.translate.instant(
                    'TIME_INPUT_VALID_TIME_RANGE_FROM_TEXT',
                    {
                        minTime: this.model.options.minValue.format(formatPattern),
                    },
                )}`,
            };
            this.warningText = <string>_.get(errors, 'minValue.text', '');
        }

        if (potentialValue.isAfter(this.model.options.maxValue)) {
            errors.maxValue = {
                text: `${this.translate.instant('TIME_INPUT_SELECT_TIME_RANGE_TEXT')} ${this.translate.instant(
                    'TIME_INPUT_VALID_TIME_RANGE_UPTO_TEXT',
                    {
                        maxTime: this.model.options.maxValue.format(formatPattern),
                    },
                )}`,
            };
            this.warningText = <string>_.get(errors, 'maxValue.text', '');
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public onKeyPress(evt: KeyboardEvent): boolean {
        return new RegExp('^[0-9]*$').test(evt.key);
    }

    public submitTimeOnEnter(): void {
        if ((!this.is12HoursFormat || this.meridiem) && this.timeInputForm.valid) {
            this.onSave();
        }
    }
}
